import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import { Document, Image, StyleSheet, Text, View } from "@react-pdf/renderer";
import { useMemo } from "react";

import { TimesheetEntry } from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import PrintNewPage from "../../print/common/NewPage";
import { getDaysInMonth } from "./form";

import JaseemSign from "@/v2/assets/signatures/jaseem.png";
import HarisSign from "@/v2/assets/signatures/haris.png";
import LuluSign from "@/v2/assets/signatures/lulu.png";
import {
    FooterConfig,
    Signer,
    TimeSheetStages,
} from "@/v2/types/TimeSheetPrint";

const styles = StyleSheet.create({
    page: {
        padding: 20,
        paddingTop: 80,
        paddingBottom: 80,
        fontSize: 8,
    },
    headerImage: {
        position: "absolute",
        top: 10,
        left: 20,
        right: 30,
        width: "90%",
    },
    projectInfoSection: {
        display: "flex",
        flexDirection: "row",
        border: "1px solid black",
        marginBottom: 10,
    },
    leftInfo: {
        width: "70%",
        padding: 8,
        borderRight: "1px solid black",
        fontSize: 8,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 15,
    },
    rightInfo: {
        width: "30%",
        padding: 8,
        fontSize: 8,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    infoRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    label: {
        fontWeight: "bold",
    },
    value: {
        fontWeight: "normal",
    },
    centerText: {
        textAlign: "center",
        marginBottom: 3,
    },
    legendSection: {
        padding: "5px",
        borderTop: "2px solid black",
        borderBottom: "2px solid black",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 15,
    },
    legendItem: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        fontSize: 7,
    },
    legendBox: {
        width: 12,
        height: 12,
        border: "1px solid black",
    },
    footerSection: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-end",
        marginTop: "8px",
        paddingTop: "5px",
        fontSize: 8,
    },
    signatureItem: {
        textAlign: "center",
        marginTop: 70,
    },
    signatureImg: {
        width: "100px",
    },
});

const cellStyles = StyleSheet.create({
    slHeader: {
        padding: "4px 2px",
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    sl: {
        padding: "4px 2px",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    nameHeader: {
        padding: "4px 2px",
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    name: {
        padding: "4px 2px",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    tradeHeader: {
        padding: "4px 2px",
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    trade: {
        padding: "4px 2px",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    iqamaHeader: {
        padding: "4px 2px",
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    iqama: {
        padding: "2px 2px",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
    date: {
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 6,
        textAlign: "center",
        justifyContent: "center",
        border: "1px solid black",
        display: "flex",
        flexDirection: "column",
    },
    grandTotal: {
        padding: "4px 2px",
        backgroundColor: "#d0d0d0",
        fontWeight: "bold",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
        display: "flex",
        flexDirection: "column",
    },
    finalRow: {
        padding: "4px 2px",
        fontSize: 7,
        textAlign: "center",
        border: "1px solid black",
    },
});

const getStatusColor = (status: string): string => {
    switch (status) {
        case "NOT_MOBILIZED":
            return "#808080";
        case "OTHER":
            return "#A3B1C6";
        case "WORKING":
            return "#FFFF00";
        case "HOLIDAY":
            return "#90EE90";
        case "ABSENT":
            return "#FFA500";
        case "DAY_OFF":
            return "#ADD8E6";
        case "SICK_LEAVE":
            return "#FFB6C1";
        case "RELEASED":
            return "#FF0000";
        case "PUNCH_MISS":
            return "#9333ea";
        case "VACATION":
            return "#fef08a";
        default:
            return "#FFFFFF";
    }
};

const getStatusDisplay = (hours: number, status: string): string => {
    if (status === "NOT_MOBILIZED") return "-";
    if (status === "FRIDAY") return "FR";
    if (status === "HOLIDAY") return "H";
    if (status === "RELEASED") return "R";
    if (status === "ABSENT") return "A";
    if (status === "DAY_OFF") return "DO";
    if (status === "SICK_LEAVE") return "SL";
    if (status === "VACATION") return "V";
    if (status === "PUNCH_MISS") return "PM";
    if (status === "OTHER") return "OT";
    return hours ? hours.toString() : "";
};

const TimesheetPDF = ({
    data,
    hasDeduction = false,
    deductionRate = 0,
    hasOtherDeduction = false,
    otherDeductionAmount = 0,
    otherDeductionReason = "",
    month,
    projectInfo,
    isDraft = false,
    comment = "",
    stage = "drafted",
}: {
    data: TimesheetEntry[];
    month: string;
    hasDeduction?: boolean;
    deductionRate?: number;
    hasOtherDeduction?: boolean;
    otherDeductionAmount?: number;
    otherDeductionReason?: string;
    projectInfo: {
        projectDescription: string;
        poNumber: string;
        projectLocation: string;
        supplierName: string;
    };
    isDraft?: boolean;
    comment?: string;
    stage: TimeSheetStages;
}) => {
    const employees = data;
    const dates = useMemo(() => getDaysInMonth(month), [month]);
    const date = new Date(`${month}-01`);
    const timeSheetMonth = date
        .toLocaleString("en-US", {
            month: "long",
            year: "numeric",
        })
        .toUpperCase();

    const numDays = dates.length;

    const baseWeightings = [0.03, 0.15, 0.08, 0.08];
    const dayWeighting = 0.0171;
    const endWeightings = [0.04, 0.04, 0.05];

    const normalizedWeightings = [
        ...baseWeightings,
        ...Array(numDays).fill(dayWeighting),
        ...endWeightings,
    ];

    let grandTotal = 0;

    let totalAbsentDays = 0;

    // Calculate total absent days across all employees
    if (hasDeduction) {
        employees.forEach((emp) => {
            const absentCount = emp.work_logs.filter(
                (log) => log.status === "ABSENT",
            ).length;
            totalAbsentDays += absentCount;
        });
    }

    const deductionAmount = hasDeduction ? totalAbsentDays * deductionRate : 0;

    let grandTotalHours = 0;

    return (
        <Document>
            <PrintNewPage orientation="landscape">
                <View>
                    <View style={styles.projectInfoSection}>
                        <View style={styles.leftInfo}>
                            <View
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "50%",
                                }}
                            >
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        Supplier Name:
                                    </Text>
                                    <Text style={styles.value}>
                                        {projectInfo.supplierName}
                                    </Text>
                                </View>
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        Project Description:
                                    </Text>
                                    <Text style={styles.value}>
                                        {projectInfo.projectDescription || ""}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>PO Number:</Text>
                                    <Text style={styles.value}>
                                        {projectInfo.poNumber || ""}
                                    </Text>
                                </View>
                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        Project Location:
                                    </Text>
                                    <Text style={styles.value}>
                                        {projectInfo.projectLocation ||
                                            "DAMMAM"}
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={styles.rightInfo}>
                            <Text
                                style={{
                                    fontWeight: "bold",
                                    fontSize: 10,
                                    textAlign: "center",
                                }}
                            >
                                TIME SHEET FOR THE MONTH OF{" "}
                                {timeSheetMonth || ""}
                            </Text>
                        </View>
                    </View>

                    <Table
                        style={{ marginBottom: 10 }}
                        weightings={normalizedWeightings}
                    >
                        <TH>
                            <TD style={cellStyles.slHeader}>NO</TD>
                            <TD style={cellStyles.nameHeader}>NAME</TD>
                            <TD style={cellStyles.tradeHeader}>TRADE</TD>
                            <TD style={cellStyles.iqamaHeader}>IQAMA</TD>
                            {dates.map((day, idx) => {
                                const date = new Date(day);
                                const isFriday = date.getDay() === 5;

                                return (
                                    <TD
                                        key={idx}
                                        style={{
                                            ...cellStyles.date,
                                            backgroundColor: isFriday
                                                ? "#EAB308"
                                                : "#d0d0d0",
                                            color: isFriday
                                                ? "#FFFFFF"
                                                : "#000000",
                                        }}
                                    >
                                        <Text>
                                            {date.toLocaleDateString("en-US", {
                                                weekday: "short",
                                            })}
                                        </Text>
                                        <Text style={{ fontWeight: "bold" }}>
                                            {date.getDate()}
                                        </Text>
                                    </TD>
                                );
                            })}
                            <TD style={cellStyles.slHeader}>Total Hours</TD>
                            <TD style={cellStyles.slHeader}>Hourly Rate</TD>
                            <TD style={cellStyles.grandTotal}>Total</TD>
                        </TH>

                        {employees.map((emp, empIdx) => {
                            const totalHours = emp.total_hours;
                            const hourlyRate = emp.hourly_rate || 0;
                            const total = totalHours * hourlyRate;
                            grandTotal += total;

                            grandTotalHours += totalHours;
                            return (
                                <TR key={empIdx} wrap={false}>
                                    <TD style={cellStyles.sl}>{empIdx + 1}</TD>
                                    <TD style={cellStyles.name}>
                                        {emp.employee_name}
                                    </TD>
                                    <TD style={cellStyles.trade}>
                                        {emp.trade}
                                    </TD>
                                    <TD style={cellStyles.iqama}>
                                        {emp.iqama}
                                    </TD>
                                    {emp.work_logs.map((day, dayIdx) => (
                                        <TD
                                            key={dayIdx}
                                            style={{
                                                textAlign: "center",
                                                display: "flex",
                                                fontSize: 6,
                                                justifyContent: "center",
                                                backgroundColor: getStatusColor(
                                                    day.status,
                                                ),
                                            }}
                                        >
                                            {getStatusDisplay(
                                                day.hours,
                                                day.status,
                                            )}
                                        </TD>
                                    ))}
                                    <TD style={cellStyles.sl}>{totalHours}</TD>
                                    <TD style={cellStyles.sl}>{hourlyRate}</TD>
                                    <TD style={cellStyles.sl}>
                                        {total.toFixed(2)}
                                    </TD>
                                </TR>
                            );
                        })}

                        <TR wrap={false}>
                            <TD
                                weighting={1}
                                style={{
                                    ...cellStyles.finalRow,
                                    fontWeight: "bold",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    fontSize: 10,
                                    width: "100%",
                                }}
                            >
                                Total Hours : {grandTotalHours}
                            </TD>
                        </TR>

                        {hasOtherDeduction && (
                            <TR wrap={false}>
                                <TD
                                    weighting={1}
                                    style={{
                                        ...cellStyles.finalRow,
                                        fontWeight: "bold",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        fontSize: 10,
                                        width: "100%",
                                    }}
                                >
                                    Total Amount :{grandTotal.toFixed(2)}
                                    SR
                                </TD>
                            </TR>
                        )}
                        {hasDeduction && (
                            <TR wrap={false}>
                                <TD
                                    weighting={1}
                                    style={{
                                        ...cellStyles.finalRow,
                                        fontWeight: "bold",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        fontSize: 10,
                                        width: "100%",
                                    }}
                                >
                                    Deduction : {deductionAmount.toFixed(2)} SR
                                </TD>
                            </TR>
                        )}

                        {hasOtherDeduction && (
                            <TR wrap={false}>
                                <TD
                                    weighting={1}
                                    style={{
                                        ...cellStyles.finalRow,
                                        fontWeight: "bold",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        fontSize: 10,
                                        width: "100%",
                                    }}
                                >
                                    Other Deduction :
                                    {otherDeductionAmount.toFixed(2)} SR
                                </TD>
                            </TR>
                        )}

                        <TR wrap={false}>
                            {isDraft ? (
                                <TD
                                    weighting={1}
                                    style={{
                                        ...cellStyles.finalRow,
                                        fontWeight: "bold",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: 10,
                                        width: "100%",
                                    }}
                                >
                                    <Text style={{ color: "white" }}>
                                        DRAFT
                                    </Text>
                                    <Text>
                                        {" "}
                                        Grand Total :
                                        {(
                                            grandTotal -
                                            deductionAmount -
                                            otherDeductionAmount
                                        ).toFixed(2)}
                                        SR
                                    </Text>
                                </TD>
                            ) : (
                                <TD
                                    weighting={1}
                                    style={{
                                        ...cellStyles.finalRow,
                                        fontWeight: "bold",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        fontSize: 10,
                                        width: "100%",
                                    }}
                                >
                                    Grand Total :
                                    {(
                                        grandTotal -
                                        deductionAmount -
                                        otherDeductionAmount
                                    ).toFixed(2)}
                                    SR
                                </TD>
                            )}
                        </TR>
                    </Table>

                    <View style={styles.legendSection}>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#FFA500" },
                                ]}
                            />
                            <Text>A - ABSENT</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#90EE90" },
                                ]}
                            />
                            <Text>H - HOLIDAY</Text>
                        </View>

                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#ADD8E6" },
                                ]}
                            />
                            <Text>DO - DAY-OFF</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#FFB6C1" },
                                ]}
                            />
                            <Text>SL - SICK LEAVE</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    {
                                        backgroundColor: "#9333ea",
                                        color: "#ffffff",
                                    },
                                ]}
                            />

                            <Text>PM - PUNCH MISS</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    {
                                        backgroundColor: "#fef08a",
                                    },
                                ]}
                            />
                            <Text>V - VACATION</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#FF0000" },
                                ]}
                            />
                            <Text>R - RELEASED</Text>
                        </View>
                        <View style={styles.legendItem}>
                            <View
                                style={[
                                    styles.legendBox,
                                    { backgroundColor: "#A3B1C6" },
                                ]}
                            />
                            <Text>OT - OTHER</Text>
                        </View>
                    </View>

                    {hasOtherDeduction && (
                        <View
                            style={{
                                marginTop: "8px",
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: "10pt",
                                    fontWeight: "800",
                                }}
                            >
                                Deduction Note
                            </Text>
                            <Text
                                style={{
                                    marginTop: "2px",
                                }}
                            >
                                {otherDeductionReason}
                            </Text>
                        </View>
                    )}

                    {comment && (
                        <View
                            style={{
                                marginTop: "8px",
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: "10pt",
                                    fontWeight: "800",
                                }}
                            >
                                Comment
                            </Text>
                            <Text
                                style={{
                                    marginTop: "2px",
                                }}
                            >
                                {comment}
                            </Text>
                        </View>
                    )}
                </View>
                <SignedFooter stage={stage} />
            </PrintNewPage>
        </Document>
    );
};

function SignedFooter({ stage }: { stage: TimeSheetStages }) {
    const signatureConfig: Record<TimeSheetStages, FooterConfig> = {
        drafted: {},
        "to-be-verified": {
            prepared: {
                name: "Muhammed Jaseem CP",
                sign: JaseemSign,
            },
        },
        verified: {
            prepared: {
                name: "Muhammed Jaseem CP",
                sign: JaseemSign,
            },
            checked: {
                name: "Haris",
                sign: HarisSign,
            },
        },
        approved: {
            prepared: {
                name: "Muhammed Jaseem CP",
                sign: JaseemSign,
            },
            checked: {
                name: "Haris",
                sign: HarisSign,
            },
            approved: {
                name: "Lulu Shifana",
                sign: LuluSign,
            },
        },
    };

    const config = signatureConfig[stage] || {};

    return (
        <View style={styles.footerSection}>
            <SignatureItem label="Prepared By" signer={config.prepared} />
            <SignatureItem label="Checked by" signer={config.checked} />
            <SignatureItem label="Approved By" signer={config.approved} />
        </View>
    );
}

function SignatureItem({ label, signer }: { label: string; signer?: Signer }) {
    return (
        <View style={styles.signatureItem}>
            {signer ? (
                <>
                    <Image src={signer.sign} style={styles.signatureImg} />
                    <Text>
                        {label} {signer.name}
                    </Text>
                </>
            ) : (
                <Text>{label} _______________</Text>
            )}
        </View>
    );
}

export default TimesheetPDF;
